import * as PopoverPrimitive from '@radix-ui/react-popover';
import React from 'react';
import sharedStyles from '../radix-floating-elements.module.scss';

// import { XClose } from '@timelyapp/icons/line-icons';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

const cvaContent = cva(undefined, {
  variants: {
    variant: {
      /** Same as `DropdownMenu` */
      dropdown: sharedStyles.dropdownContentBase,
      /** More spacing, floats higher than default dropdown */
      popover: sharedStyles.popoverContentBase,
    },
  },
  defaultVariants: {
    variant: 'dropdown',
  },
});

export type DropdownProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Root> &
  VariantProps<typeof cvaContent> & {
    className?: string;
    trigger: React.ReactElement;
    /** Contents of this dropdown */
    children: React.ReactNode;
    onEscapeKeyDown?: (event: KeyboardEvent) => void;
  } & Pick<React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>, 'align'>;

/**
 * PURPOSE:
 * A DX & user-friendly dropdown built on Radix's via PopoverPrimitive
 *
 * @note We'll probably need overrides to adjust the sizing/padding, or consider Popover as base instead.
 *
 * @link https://www.radix-ui.com/primitives/docs/components/dropdown-menu
 */
export const Dropdown = (props: DropdownProps) => {
  const { trigger, children, variant, align, ...rest } = props;

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    props.onOpenChange?.(false);
    props?.onEscapeKeyDown?.(event);
  };

  return (
    <PopoverPrimitive.Root modal {...rest}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          arrowPadding={4 * 4}
          collisionPadding={4 * 4}
          onEscapeKeyDown={handleEscapeKeyDown}
          className={cvaContent({ variant, className: props.className })}
          align={align}
        >
          {children}
          <PopoverPrimitive.Arrow className={sharedStyles.pointerArrow} />
          {/** @todo add props for this, but streamline with all other components that should control this */}
          {/* <PopoverPrimitive.PopoverClose className={sharedStyles.closeButton}>
            <XClose />
          </PopoverPrimitive.PopoverClose> */}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
Dropdown.displayName = 'Dropdown';

export default Dropdown;
